import { EMainProductCodeType } from '@aqx-core/common/api/config';
import { ESymbolCategory } from '@aqx-core/common/api/market';
import { EWatchlistCategory } from '@aqx-core/common/api/watchlist';
import { TAppLanguageISOCountryCode, i18n } from '@aqx-core/common/i18n';
import { companyService } from '@aqx-core/common/services/company';
import { mapSymbolCategoryToLabel } from '@aqx-core/common/utils/symbol';

import { EMarketComponentItems } from './types';

export const mapMarketComponentItemsToLabel = (item: EMarketComponentItems, lng?: TAppLanguageISOCountryCode) => {
  const MARKET_COMPONENT_ITEMS_MAP: Record<EMarketComponentItems, string> = {
    [EMarketComponentItems.ALL]: i18n.t('Watchlist', { lng }),
    [EMarketComponentItems.FAVOURITES]: i18n.t('My Favorites', { lng }),
    [EMarketComponentItems.TOP_PICK]: i18n.t('Top Picks', { lng }),
    [EMarketComponentItems.TOP_GAINER]: i18n.t('Top Gainers', { lng }),
    [EMarketComponentItems.TOP_LOSER]: i18n.t('Top Losers', { lng }),
    [EMarketComponentItems.POSITIONS]: i18n.t('My Positions', { lng }),
    [EMarketComponentItems.SIGNALS]: i18n.t('Signals', { lng })
  };
  return MARKET_COMPONENT_ITEMS_MAP[item];
};

export const marketComponentConvertion = (component: EMarketComponentItems) => {
  const MARKET_COMPONENT_CONVERTION: Partial<Record<EMarketComponentItems, EWatchlistCategory>> = {
    [EMarketComponentItems.FAVOURITES]: EWatchlistCategory.FAVOURITES,
    [EMarketComponentItems.TOP_PICK]: EWatchlistCategory.TOP_PICK,
    [EMarketComponentItems.TOP_GAINER]: EWatchlistCategory.TOP_GAINER,
    [EMarketComponentItems.TOP_LOSER]: EWatchlistCategory.TOP_LOSER
  };
  return MARKET_COMPONENT_CONVERTION[component];
};

export const marketSymbolCategoryFilters = (symbolCategories: ESymbolCategory[], lng?: TAppLanguageISOCountryCode) => {
  const allFilter = { label: mapSymbolCategoryToLabel(ESymbolCategory.ALL, lng), value: ESymbolCategory.ALL };
  const filters: (typeof allFilter)[] = [];
  const categories =
    companyService.getCompanyIntegration() === EMainProductCodeType.HORIZON
      ? [ESymbolCategory.EQUITIES, ESymbolCategory.OPTION, ESymbolCategory.ETFS, ESymbolCategory.FUTURES]
      : [
          ESymbolCategory.COMMODITIES,
          ESymbolCategory.CRYPTO,
          ESymbolCategory.FOREX,
          ESymbolCategory.INDEX,
          ESymbolCategory.SHARES
        ];
  categories.forEach((category) => {
    if (symbolCategories.includes(category)) {
      filters.push({ label: mapSymbolCategoryToLabel(category, lng), value: category });
    }
  });

  return [allFilter, ...filters.sort((a, b) => a.label.localeCompare(b.label))];
};

export const getEmptyStateLabel = (type: EMarketComponentItems, lng?: TAppLanguageISOCountryCode): string => {
  switch (type) {
    case EMarketComponentItems.FAVOURITES:
      return i18n.t('There are no favorites.', { lng });
    case EMarketComponentItems.POSITIONS:
      return i18n.t('There are no trades.', { lng });
    case EMarketComponentItems.SIGNALS:
      return i18n.t('There are no signals.', { lng });
    default:
      return i18n.t('There are no data.', { lng });
  }
};

export const getDescriptionWidth = (componentType: EMarketComponentItems, viewportWidth: number): string => {
  // Define viewport breakpoints
  const isSmallViewport = viewportWidth < 1500;
  const isLargeViewport = viewportWidth > 1800;

  if (componentType === EMarketComponentItems.SIGNALS) {
    if (isSmallViewport) {
      return '120px';
    }
    if (!isLargeViewport) {
      return '150px'; // Medium viewport (1500-1800px)
    }
    return '180px';
  }

  const widthConfig = {
    [EMarketComponentItems.TOP_PICK]: { small: '130px', large: '250px' },
    [EMarketComponentItems.TOP_GAINER]: { small: '130px', large: '250px' },
    [EMarketComponentItems.TOP_LOSER]: { small: '130px', large: '250px' },
    [EMarketComponentItems.FAVOURITES]: { small: '130px', large: '250px' },
    [EMarketComponentItems.POSITIONS]: { small: '200px', large: '250px' },
    [EMarketComponentItems.ALL]: { small: '200px', large: '250px' }
  };

  const config = widthConfig[componentType];

  if (!config) {
    return '200px'; // Default fallback
  }

  return isSmallViewport ? config.small : config.large;
};
